import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import { useField } from 'formik';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';
import Paragraph from 'packages/kakapo-ui-kit/components/common/Paragraph';
import PopoverTarget from 'packages/kakapo-ui-kit/components/popovers/PopoverTarget';

const Bullet = styled(Flex)<{
	filled?: boolean;
	party: graphql.EvaluationSubmissionParty;
	enabled: boolean;
}>`
	width: 32px;
	height: 32px;
	border-radius: 16px;
	border: 1px solid #dae0e8;
	overflow: hidden;

	${(props) => (props.enabled ? `cursor: pointer` : `opacity: 0.25`)};

	${(props) =>
		props.filled &&
		`border: none; background: ${
			props.party === graphql.EvaluationSubmissionParty.Employee
				? props.theme.colors.employee
				: props.theme.colors.manager
		}`};
`;

const Dot = styled.div<{ filled: boolean }>`
	position: absolute;
	left: 12px;
	top: 12px;
	width: 8px;
	height: 8px;
	background: ${(props) => (props.filled ? 'rgba(0, 0, 0, 0.1)' : '#dae0e8')};
	border-radius: 8px;
`;

const Label = styled.div`
	color: ${(props) => props.theme.colors.slateBlue};
	text-overflow: ellipsis;
	overflow: hidden;
`;

const TooltipTitle = styled.div`
	border-top: 1px solid ${(props) => props.theme.colors.lightBlue};
	padding-top: 10px;
	margin: 0px -20px;
	margin-top: 10px !important;
	margin-bottom: -5px;
	text-align: center;
`;

function CompetencyInputLevel(props: {
	index: number;
	name: string;
	level: graphql.CompetencyLevelOption;
	party: graphql.EvaluationSubmissionParty;
	suffix?: string;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();

	const [, { value }, { setValue }] = useField(props.name + (props.suffix ?? '.competency'));

	return (
		<Box
			mx={1}
			style={{ position: 'relative' }}
			onClick={() => props.level.enabled && setValue(props.index)}
		>
			<PopoverTarget
				enabled={props.level.enabled}
				content={
					<Box style={{ padding: 20 }}>
						<Paragraph color="slateBlue">
							<QuillText
								quill={localize(props.level.criteria ?? [])?.text}
								placeholder={t('evaluation.submission.competency.levels.noDescription')}
							/>
						</Paragraph>
						<TooltipTitle>{localize(props.level.level.title)?.text}</TooltipTitle>
					</Box>
				}
			>
				<Bullet filled={value === props.index} party={props.party} enabled={props.level.enabled}>
					<Dot filled={value === props.index} />
				</Bullet>
			</PopoverTarget>
		</Box>
	);
}

export function CompetencyLevelsInput(props: {
	name: string;
	levels: Array<graphql.CompetencyLevelOption>;
	party: graphql.EvaluationSubmissionParty;
	suffix?: string;
}) {
	const { localize } = useLocalize();

	const { levels } = props;
	// Use enabled levels for the left and right legend text.
	const enabledLevels = levels.filter((level) => !!level.enabled);

	const bullets = levels
		.map<[graphql.CompetencyLevelOption, number]>((level, i) => [level, i])
		.filter(([level]) => level.enabled)
		.map(([level, i]) => (
			<CompetencyInputLevel
				key={i}
				index={i}
				level={level}
				name={props.name}
				suffix={props.suffix}
				party={props.party}
			/>
		));

	return (
		<Flex alignItems="center" justifyContent="space-between">
			{enabledLevels.length > 0 && (
				<Box mr={2}>
					<Label>{localize(enabledLevels[0].level.title).text}</Label>
				</Box>
			)}
			{bullets}
			{enabledLevels.length > 0 && (
				<Box ml={2}>
					<Label>{localize(enabledLevels[enabledLevels.length - 1].level.title).text}</Label>
				</Box>
			)}
		</Flex>
	);
}

export default function CompetencyInput(props: {
	name: string;
	initialValue?: number;
	competency: graphql.EvaluationCompetencyItem;
	party: graphql.EvaluationSubmissionParty;
}) {
	return (
		<CompetencyLevelsInput
			name={props.name}
			suffix=""
			levels={props.competency.competency.levels}
			party={props.party}
		/>
	);
}
